/**
 * Turning points and lines into things that stand up.
 *
 * Doors were billboard sprites and fences were a line drawn at fence height.
 * Both are flat, and flat is what made them read wrong: a door was a pale
 * rectangle lying against a wall, a garage was the same rectangle slightly
 * wider, and a fence was a thread hovering above the ground with nothing under
 * it. None of them looked like the thing it marked.
 *
 * A door is roughly a metre wide, forty centimetres deep and two metres tall.
 * Give it that footprint and that height and extrude it, and it reads as a door
 * without any artwork at all — the geometry does the work. Same for a garage,
 * which is simply wider and a little taller, and for a fence, which is a very
 * thin very long one.
 */

/** Metres per degree of latitude. Constant enough at city scale. */
const M_PER_DEG_LAT = 110540;

/** Metres per degree of longitude, which is not constant — it shrinks poleward. */
const mPerDegLng = (lat) => 111320 * Math.cos((lat * Math.PI) / 180);

/**
 * How big each kind of opening is, in metres.
 *
 * Real dimensions, because the point is that they look like themselves. A
 * garage is what you can tell apart at a glance: three times the width of a
 * door and half a metre taller.
 */
export const OPENING = {
  door: { width: 1.0, depth: 0.35, height: 2.1 },
  service: { width: 3.2, depth: 0.4, height: 2.5 },
  gate: { width: 2.6, depth: 0.25, height: 1.9 },
  exit: { width: 1.0, depth: 0.35, height: 2.1 },
};

export const openingOf = (kind) => OPENING[kind] ?? OPENING.door;

/**
 * A rectangular footprint centred on a point, turned to face along a wall.
 *
 * `bearing` is the direction of the wall in radians, measured the same way
 * atan2(dLng, dLat) gives it. The rectangle's width runs along the wall and its
 * depth across it, so the slab sits in the wall rather than across the doorway.
 */
export function slab({ lng, lat, bearing = 0, width, depth }) {
  const lngScale = mPerDegLng(lat);
  const halfW = width / 2;
  const halfD = depth / 2;
  const cos = Math.cos(bearing);
  const sin = Math.sin(bearing);

  // Along-wall and across-wall unit vectors, in degrees.
  const alongLng = (sin * halfW) / lngScale;
  const alongLat = (cos * halfW) / M_PER_DEG_LAT;
  const acrossLng = (cos * halfD) / lngScale;
  const acrossLat = (-sin * halfD) / M_PER_DEG_LAT;

  return [[
    [lng - alongLng - acrossLng, lat - alongLat - acrossLat],
    [lng + alongLng - acrossLng, lat + alongLat - acrossLat],
    [lng + alongLng + acrossLng, lat + alongLat + acrossLat],
    [lng - alongLng + acrossLng, lat - alongLat + acrossLat],
    [lng - alongLng - acrossLng, lat - alongLat - acrossLat],
  ]];
}

/** Direction of the line from a to b, for feeding back into slab(). */
export function bearingOf([aLng, aLat], [bLng, bLat]) {
  const dLng = (bLng - aLng) * mPerDegLng(aLat);
  const dLat = (bLat - aLat) * M_PER_DEG_LAT;
  return Math.atan2(dLng, dLat);
}

/**
 * A run of fence, as one extrudable footprint per segment.
 *
 * Per segment rather than one buffered polygon for the whole path: a corner
 * would need mitring, and a fence with square butt joints at the corners is
 * indistinguishable from a mitred one at the scale anybody looks at this.
 */
export function fencePanels(path, thickness = 0.3) {
  const panels = [];
  if (!Array.isArray(path) || path.length < 2) return panels;

  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i];
    const b = path[i + 1];
    if (!Array.isArray(a) || !Array.isArray(b)) continue;

    const midLat = (a[1] + b[1]) / 2;
    const lngScale = mPerDegLng(midLat);
    const dLng = (b[0] - a[0]) * lngScale;
    const dLat = (b[1] - a[1]) * M_PER_DEG_LAT;
    const len = Math.hypot(dLng, dLat);
    // Rounding coordinates to ~1m leaves duplicate points behind; a zero-length
    // segment has no direction to offset along and tessellates to nothing.
    if (len < 0.01) continue;

    // Unit normal to the segment, in degrees.
    const nLng = ((-dLat / len) * (thickness / 2)) / lngScale;
    const nLat = ((dLng / len) * (thickness / 2)) / M_PER_DEG_LAT;

    panels.push([[
      [a[0] + nLng, a[1] + nLat],
      [b[0] + nLng, b[1] + nLat],
      [b[0] - nLng, b[1] - nLat],
      [a[0] - nLng, a[1] - nLat],
      [a[0] + nLng, a[1] + nLat],
    ]]);
  }
  return panels;
}
